import React from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import BasicLayout from "components/Layouts/BasicLayout";
import GeneralTab from './GeneralTab';
import PhoneNumbersTab from './PhoneNumbersTab';
import SubscriptionTab from './SubscriptionTab';
import OrganizationTemplates from './OrganizationTemplates';
import ApiKeysTab from './ApiKeysTab';
import Invoices from './Invoices';
import StripeConnectTab from './StripeConnectTab';
import TwilioTab from './TwilioTab';
import EmailTab from './EmailTab';
import { useNavigate } from 'react-router-dom';
import { OrganizationProvider, useOrganization } from './OrganizationContext';

const OrganizationContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { saveOrganization, loading } = useOrganization();

  const pathName = location.pathname.split('/').pop();
  const tabPaths = {
    general: '/organization/general',
    phones: '/organization/phones',
    subscription: '/organization/subscription',
    "api-keys": '/organization/api-keys',
    invoices: '/organization/invoices',
    "stripe-connect": '/organization/stripe-connect',
    templates: '/organization/templates',
    twilio: '/organization/twilio',
    email: '/organization/email'
  };

  if (!tabPaths[pathName]) {
    return <Navigate to="/organization/general" replace />;
  }

  const currentTab = tabPaths[pathName] ? pathName : 'general';

  // Define tab order and labels for mobile navigation
  const tabOrder = ['general', 'phones', 'subscription', 'invoices', 'templates', 'api-keys', 'stripe-connect', 'twilio', 'email'];
  const tabLabels = {
    general: 'General',
    phones: 'Phone Numbers',
    subscription: 'Subscription',
    'api-keys': 'API Keys',
    invoices: 'Invoices',
    'stripe-connect': 'Stripe Connect',
    templates: 'Default Templates',
    twilio: 'Twilio',
    email: 'Email'
  };

  const getCurrentTabIndex = () => {
    return tabOrder.indexOf(currentTab) + 1;
  };

  const goToPreviousTab = () => {
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex > 0) {
      navigate(tabPaths[tabOrder[currentIndex - 1]]);
    }
  };

  const goToNextTab = () => {
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex < tabOrder.length - 1) {
      navigate(tabPaths[tabOrder[currentIndex + 1]]);
    }
  };

  return (
    <BasicLayout>
      <div className="flex items-center w-full">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <h1 className="text-2xl font-semibold">Organization</h1>
        <div className="ml-auto">
          <button
            className={`px-4 py-2 rounded bg-blue-600 text-white ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
            onClick={saveOrganization}
            disabled={loading}
          >
            Save
          </button>
        </div>
      </div>

      {/* Desktop Tabs */}
      <div className="hidden md:block border-b border-gray-200">
        <nav className="flex space-x-8">
          <Link
            to={tabPaths.general}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'general' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            General
          </Link>
          <Link
            to={tabPaths.subscription}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'subscription' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Subscription
          </Link>
          <Link
            to={tabPaths.invoices}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'invoices' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Invoices
          </Link>
          <Link
            to={tabPaths.templates}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'templates' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Default Templates
          </Link>
          <Link
            to={tabPaths["api-keys"]}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'api-keys' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            API Keys
          </Link>
          <Link
            to={tabPaths["stripe-connect"]}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'stripe-connect' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Stripe Connect
          </Link>
          <Link
            to={tabPaths.twilio}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'twilio' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Phone Numbers
          </Link>
          <Link
            to={tabPaths.email}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'email' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
          >
            Email
          </Link>
        </nav>
      </div>

      {/* Mobile Tab Navigation */}
      <div className="block md:hidden border-b border-gray-200">
        <div className="flex items-center justify-between py-2">
          <button
            onClick={goToPreviousTab}
            disabled={tabOrder.indexOf(currentTab) === 0}
            className={`p-2 rounded-full ${tabOrder.indexOf(currentTab) === 0 ? 'text-gray-300' : 'text-blue-600'}`}
          >
            ←
          </button>
          <div className="font-medium">
            {tabLabels[currentTab]} ({getCurrentTabIndex()}/{tabOrder.length})
          </div>
          <button
            onClick={goToNextTab}
            disabled={tabOrder.indexOf(currentTab) === tabOrder.length - 1}
            className={`p-2 rounded-full ${tabOrder.indexOf(currentTab) === tabOrder.length - 1 ? 'text-gray-300' : 'text-blue-600'}`}
          >
            →
          </button>
        </div>
      </div>

      <Routes>
        <Route path="general" element={<GeneralTab />} />
        <Route path="subscription" element={<SubscriptionTab />} />
        <Route path="api-keys" element={<ApiKeysTab />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="stripe-connect" element={<StripeConnectTab />} />
        <Route path="templates" element={<OrganizationTemplates />} />
        <Route path="twilio" element={<TwilioTab />} />
        <Route path="email" element={<EmailTab />} />
        <Route path="/" element={<Navigate to="general" replace />} />
      </Routes>
    </BasicLayout>
  );
};

const Organization: React.FC = () => {
  return (
    <OrganizationProvider>
      <OrganizationContent />
    </OrganizationProvider>
  );
};

export default Organization;